import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import MovieCardCompact from "@/pages/search/components/MovieCardCompact";
import { useSeo } from "@/hooks/useSeo";
import { categories, searchMovies } from "@/utils/movie";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(q);

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const results = useMemo(() => searchMovies(q), [q]);

  useSeo({
    title: q ? `搜索“${q}”结果 - 免费在线电影` : "搜索影片 - 免费在线电影",
    description: q
      ? `免费在线电影为你找到与“${q}”相关的影片共 ${results.length} 部，涵盖标题、导演、主演与类型匹配结果。`
      : "在免费在线电影搜索你想看的高清影片。",
    keywords: `搜索电影,${q || "影片搜索"},免费在线电影,高清电影`,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = keyword.trim();
    if (next) setParams({ q: next });
    else setParams({});
  };

  return (
    <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-accent-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">搜索</span>
      </nav>

      <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
        {q ? <>搜索“<span className="text-accent-600">{q}</span>”的结果</> : "搜索影片"}
      </h1>

      <form onSubmit={submit} className="mt-6 flex w-full max-w-2xl flex-col gap-3 sm:flex-row">
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 focus-within:border-accent-400">
          <i className="ri-search-line text-foreground-400"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="输入电影名称、导演或主演..."
            className="w-full bg-transparent py-3 text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
          />
        </div>
        <button
          type="submit"
          className="flex items-center justify-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
        >
          <i className="ri-search-line"></i>
          搜索
        </button>
      </form>

      {q && (
        <p className="mt-6 text-sm text-foreground-600">
          共找到 <span className="font-semibold text-accent-600">{results.length}</span> 部相关影片
        </p>
      )}

      {results.length > 0 ? (
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
          {results.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <p className="mt-4 font-heading text-lg font-semibold text-foreground-800">
            {q ? `没有找到与“${q}”相关的影片` : "还没有输入搜索关键词"}
          </p>
          <p className="mt-1 text-sm text-foreground-500">试试其他关键词，或从下面的热门分类中挑选</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {categories.slice(0, 6).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-1.5 rounded-full bg-secondary-100 px-4 py-1.5 text-sm text-secondary-900 transition-colors hover:bg-accent-100 hover:text-accent-800"
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
          <Link
            to="/"
            className="mt-6 rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      )}

      {results.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center gap-2">
            <i className="ri-magic-line text-xl text-accent-500"></i>
            <h2 className="font-heading text-lg font-bold text-foreground-950">你可能也喜欢</h2>
          </div>
          <div className="mt-4 flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50 px-4">
            {results.slice(0, 3).map((movie) => (
              <MovieCardCompact key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}